import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, Infinity as InfinityIcon, Coins } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Header pill showing "347/500 credits" (+ topup balance if any).
 * Color shifts to yellow at 70% used, red at 90%. Admin sees UNLIMITED.
 */
export function UsageMeter() {
    const { user } = useAuth();
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await client.get("/usage/me");
                if (!cancelled) setUsage(r.data);
            } catch { /* ignore */ }
        };
        load();
        const refresh = () => load();
        window.addEventListener("rf_usage_refresh", refresh);
        window.addEventListener("rf_upgrade_nag", refresh);
        const interval = setInterval(load, 30_000);
        return () => { cancelled = true; clearInterval(interval); window.removeEventListener("rf_usage_refresh", refresh); window.removeEventListener("rf_upgrade_nag", refresh); };
    }, [user?.id]);

    if (!user || !usage) return null;
    const c = usage.credits || {};

    // Admin / unlimited
    if (c.is_admin) {
        return (
            <Link
                to="/pricing"
                data-testid="usage-meter"
                className="hidden sm:inline-flex items-center gap-1.5 border border-primary/40 bg-primary/5 text-primary text-[11px] font-mono px-2.5 py-1"
                title="Admin — unlimited credits"
            >
                <InfinityIcon className="w-3 h-3" /> UNLIMITED
            </Link>
        );
    }

    const monthlyPct = c.monthly_limit ? Math.min(100, Math.round((c.monthly_used / c.monthly_limit) * 100)) : 0;
    const tone =
        monthlyPct >= 90 ? "border-destructive/60 bg-destructive/5 text-destructive"
      : monthlyPct >= 70 ? "border-warning/60 bg-warning/5 text-warning"
      : "border-border bg-card/40 text-muted-foreground";

    return (
        <Link
            to="/pricing"
            data-testid="usage-meter"
            title={`${c.total_available} credits available (${c.monthly_remaining} monthly + ${c.topup_balance} topup). Click to see plans & credit packs.`}
            className={`hidden sm:inline-flex items-center gap-1.5 border text-[11px] font-mono px-2.5 py-1 hover:opacity-90 ${tone}`}
        >
            <Coins className="w-3 h-3" />
            <span data-testid="usage-credits">
                <span className="text-foreground">{c.monthly_used}</span>
                <span className="opacity-60">/{c.monthly_limit}</span> credits
            </span>
            {c.topup_balance > 0 && (
                <>
                    <span className="opacity-30">·</span>
                    <span className="text-primary" data-testid="usage-topup">+{c.topup_balance} pack</span>
                </>
            )}
            {usage.tier === "free" && (
                <>
                    <span className="opacity-30">·</span>
                    <span className="text-primary inline-flex items-center gap-1"><Zap className="w-3 h-3" />Upgrade</span>
                </>
            )}
        </Link>
    );
}
